import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiErrorDetail, GOLD, computePace } from "@/lib/apiClient";
import { ReportDocument } from "@/components/ReportDocument";
import { ProgressBar } from "@/components/ProgressBar";
import { JobStatusBadge } from "@/components/Badges";
import { ZentaraMark } from "@/components/Logo";
import { Lock, Loader2, Download, ArrowLeft, FileStack, CheckCircle2, Send, Clock } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

export default function PublicDashboard() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [passcode, setPasscode] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    api.get(`/public/verify/${token}`).then((r) => setMeta(r.data)).catch(() => setNotFound(true));
  }, [token]);

  const unlock = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try { const { data } = await api.get(`/public/projects/${token}`, { params: { passcode } }); setData(data); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || "Access denied"); }
    finally { setLoading(false); }
  };

  const openReport = async (rid) => {
    setViewing("loading");
    try {
      const url = rid ? `/public/projects/${token}/reports/${rid}` : null;
      if (rid) { const { data: rep } = await api.get(url, { params: { passcode } }); setViewing(rep); }
      else setViewing(data); // current live report
    } catch { setViewing(null); }
  };

  if (notFound) return (
    <div className="flex min-h-screen items-center justify-center bg-background grid-bg px-4">
      <div className="text-center"><Lock className="mx-auto h-10 w-10 text-neutral-700" /><p className="mt-4 font-heading text-lg text-neutral-300">Dashboard not available</p><p className="mt-1 text-sm text-neutral-500">This link is invalid or sharing has been disabled.</p></div>
    </div>
  );

  if (!data) return (
    <div className="flex min-h-screen items-center justify-center bg-background grid-bg px-4">
      <div className="w-full max-w-sm rounded-xl border border-white/10 bg-[#1a1a1a] p-8">
        <div className="mb-6"><ZentaraMark size={48} /></div>
        <h1 className="font-heading text-2xl font-bold text-[#f7f3ec]">{meta?.name || "Progress Dashboard"}</h1>
        <p className="mt-1 text-sm text-neutral-400">{meta?.client_name}</p>
        <form onSubmit={unlock} className="mt-6 space-y-3">
          <label className="block text-xs font-semibold uppercase tracking-wide text-neutral-400">Enter Passcode</label>
          <input data-testid="public-passcode-input" value={passcode} onChange={(e) => setPasscode(e.target.value)} className="w-full rounded-lg border border-white/15 bg-[#141414] px-3 py-2.5 text-sm text-[#f7f3ec] outline-none focus:border-[#c5a24a] focus:ring-1 focus:ring-[#c5a24a]" placeholder="Passcode" autoFocus />
          {error && <div data-testid="public-error" className="rounded-lg border border-red-900/50 bg-red-950/40 px-3 py-2 text-sm text-red-300">{error}</div>}
          <button data-testid="public-unlock-btn" disabled={loading} className="gold-gradient flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-[#121212] transition-opacity hover:opacity-90 disabled:opacity-60">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} View Dashboard</button>
        </form>
      </div>
    </div>
  );

  // Inline printable report (only when explicitly opened / downloaded)
  if (viewing === "loading") return <div className="flex min-h-screen items-center justify-center bg-neutral-900"><Loader2 className="h-6 w-6 animate-spin text-[#c5a24a]" /></div>;
  if (viewing) return (
    <div className="min-h-screen bg-neutral-900 py-8">
      <div className="no-print mx-auto mb-6 flex max-w-[8.5in] items-center justify-between px-2">
        <button onClick={() => setViewing(null)} className="flex items-center gap-1.5 text-sm font-semibold text-neutral-400 hover:text-[#c5a24a]"><ArrowLeft className="h-4 w-4" /> Back to dashboard</button>
        <button onClick={() => window.print()} className="gold-gradient flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-[#121212] hover:opacity-90"><Download className="h-4 w-4" /> Download PDF</button>
      </div>
      {viewing._published && <div className="no-print mx-auto mb-3 max-w-[8.5in] px-2 text-xs text-neutral-500">Version {viewing._published.version} · {viewing._published.date} · {viewing._published.description}</div>}
      <ReportDocument report={viewing} />
    </div>
  );

  const g = data.grand || {};
  const remaining = Math.max((g.received || 0) - (g.completed || 0), 0);
  const chart = (data.snapshots || []).map((s) => ({ week: s.week_key.includes("-W") ? `W${s.week_key.split("-W")[1]}` : s.week_key, progress: s.overall_percent }));
  const pace = computePace(data.snapshots);
  const weekly = data.published_reports || [];

  const Kpi = ({ label, value, icon: Icon, color }) => (
    <div className="rounded-xl border border-white/10 bg-[#1a1a1a] p-5">
      <div className="flex items-center justify-between"><span className="text-xs font-semibold uppercase tracking-widest text-neutral-500">{label}</span><Icon className="h-4 w-4" style={{ color: color || "#c5a24a" }} /></div>
      <div className="mt-3 font-heading text-3xl font-bold tabular-nums text-[#f7f3ec]">{value}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background grid-bg" data-testid="public-dashboard">
      <header className="border-b border-white/10 bg-[#141414]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <ZentaraMark size={38} />
            <div className="leading-tight"><div className="font-heading text-sm font-bold text-[#f7f3ec]">Zentara <span className="text-[#c5a24a]">Redline</span></div><div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500">Client Progress Dashboard</div></div>
          </div>
          <button onClick={() => openReport(null)} data-testid="download-report-btn" className="gold-gradient flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold text-[#121212] hover:opacity-90"><Download className="h-4 w-4" /> Download Report (PDF)</button>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-6 py-8">
        {/* Hero */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3"><h1 className="font-heading text-3xl font-bold tracking-tight text-[#f7f3ec]">{data.name}</h1><JobStatusBadge status={data.status} /></div>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-neutral-500">
              <span className="font-semibold text-neutral-400">{data.job_number}</span><span className="text-neutral-300">{data.client_name}</span>{data.project_manager && <span>PM: {data.project_manager}</span>}
            </div>
          </div>
          <div className="text-right"><div className="font-heading text-5xl font-bold tabular-nums text-[#c5a24a]">{data.overall_percent}%</div><div className="text-[10px] uppercase tracking-widest text-neutral-500">Overall Complete</div></div>
        </div>

        <ProgressBar value={data.overall_percent} showLabel={false} className="mt-4" />

        {/* KPIs */}
        <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Kpi label="Received" value={g.received || 0} icon={FileStack} />
          <Kpi label="Completed" value={g.completed || 0} icon={CheckCircle2} color="#22c55e" />
          <Kpi label="Returned" value={g.returned || 0} icon={Send} color="#60a5fa" />
          <Kpi label="Remaining" value={remaining} icon={Clock} color="#f59e0b" />
        </div>

        {/* Charts */}
        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="rounded-xl border border-white/10 bg-[#1a1a1a] p-6">
            <div className="mb-3 text-xs font-semibold uppercase tracking-widest text-neutral-500">Week-over-Week Progress</div>
            {chart.length > 0 ? (
              <ResponsiveContainer width="100%" height={230}>
                <LineChart data={chart} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" /><XAxis dataKey="week" stroke="#737373" fontSize={11} tickLine={false} /><YAxis stroke="#737373" fontSize={11} tickLine={false} domain={[0, 100]} />
                  <Tooltip contentStyle={{ background: "#1a1a1a", border: "1px solid #333", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#a3a3a3" }} />
                  <Line type="monotone" dataKey="progress" stroke={GOLD} strokeWidth={2.5} dot={{ r: 3, fill: GOLD }} name="Progress %" />
                </LineChart>
              </ResponsiveContainer>
            ) : <div className="flex h-[230px] items-center justify-center text-sm text-neutral-600">Progress history will appear here.</div>}
          </div>
          <div className="rounded-xl border border-white/10 bg-[#1a1a1a] p-6">
            <div className="mb-3 text-xs font-semibold uppercase tracking-widest text-neutral-500">Pace — Drawings Completed / Week</div>
            {pace.length > 0 ? (
              <ResponsiveContainer width="100%" height={230}>
                <BarChart data={pace} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" /><XAxis dataKey="week" stroke="#737373" fontSize={11} tickLine={false} /><YAxis stroke="#737373" fontSize={11} tickLine={false} allowDecimals={false} />
                  <Tooltip cursor={{ fill: "rgba(197,162,74,0.08)" }} contentStyle={{ background: "#1a1a1a", border: "1px solid #333", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#a3a3a3" }} />
                  <Bar dataKey="pace" fill={GOLD} radius={[3, 3, 0, 0]} name="Drawings / week" />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="flex h-[230px] items-center justify-center text-sm text-neutral-600">Pace appears as weeks accumulate.</div>}
          </div>
        </div>

        {/* Area progress */}
        <div className="mt-8">
          <h2 className="mb-4 font-heading text-xl font-bold text-[#f7f3ec]">Progress by Area</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {(data.areas || []).map((a) => (
              <div key={a.id} className="rounded-xl border border-white/10 bg-[#1a1a1a] p-5">
                <div className="flex items-center justify-between"><h3 className="font-heading text-base font-bold text-[#f7f3ec]">{a.name}</h3><span className="rounded-full bg-[#c5a24a]/15 px-2 py-0.5 text-xs font-semibold text-[#c5a24a]">{a.percent}%</span></div>
                <ProgressBar value={a.percent} showLabel={false} className="mt-3" />
                <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
                  <div><div className="text-neutral-500">Received</div><div className="font-semibold tabular-nums text-neutral-200">{a.totals?.received || 0}</div></div>
                  <div><div className="text-neutral-500">Completed</div><div className="font-semibold tabular-nums text-green-400">{a.totals?.completed || 0}</div></div>
                  <div><div className="text-neutral-500">Returned</div><div className="font-semibold tabular-nums text-blue-400">{a.totals?.returned || 0}</div></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Weekly reports */}
        {weekly.length > 0 && (
          <div className="mt-8 rounded-xl border border-white/10 bg-[#1a1a1a] p-5" data-testid="weekly-reports">
            <div className="mb-3 text-xs font-semibold uppercase tracking-widest text-neutral-500">Weekly Reports</div>
            <div className="divide-y divide-white/5">
              {weekly.map((r) => (
                <div key={r.id} className="flex items-center justify-between py-2.5">
                  <div><span className="font-semibold text-[#c5a24a]">{r.version}</span><span className="ml-3 text-sm text-neutral-300">{r.description}</span><span className="ml-3 text-xs text-neutral-500">{r.date} · {r.overall_percent}%</span></div>
                  <button onClick={() => openReport(r.id)} data-testid={`open-weekly-${r.id}`} className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-neutral-300 hover:bg-white/5"><Download className="h-3.5 w-3.5" /> Open / Download</button>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="mt-8 text-center text-xs italic text-neutral-600">Zentara As-Built &amp; Engineering Group Ltd — Excellence grounded in strength and integrity.</p>
      </div>
    </div>
  );
}
